// src/currency.ts
var CURRENCIES = {
  AED: { exponent: 2, name: "UAE dirham" },
  USD: { exponent: 2, name: "US dollar" },
  EUR: { exponent: 2, name: "Euro" },
  GBP: { exponent: 2, name: "Pound sterling" },
  INR: { exponent: 2, name: "Indian rupee" },
  SAR: { exponent: 2, name: "Saudi riyal" },
  QAR: { exponent: 2, name: "Qatari riyal" },
  OMR: { exponent: 3, name: "Omani rial" },
  KWD: { exponent: 3, name: "Kuwaiti dinar" },
  BHD: { exponent: 3, name: "Bahraini dinar" },
  JPY: { exponent: 0, name: "Japanese yen" }
};
function isCurrencyCode(value) {
  return Object.prototype.hasOwnProperty.call(CURRENCIES, value);
}
function exponentOf(currency) {
  return CURRENCIES[currency].exponent;
}

// src/errors.ts
var LedgerError = class extends Error {
  constructor(code, message, details = {}) {
    super(message);
    this.code = code;
    this.details = details;
  }
  code;
  details;
  name = "LedgerError";
};

// src/decimal.ts
var DECIMAL_RE = /^(-)?(\d+)(?:\.(\d+))?$/;
var Decimal = class _Decimal {
  constructor(digits, scale) {
    this.digits = digits;
    this.scale = scale;
    Object.freeze(this);
  }
  digits;
  scale;
  static parse(text) {
    const m = DECIMAL_RE.exec(text.trim());
    if (!m) throw new LedgerError("DECIMAL_PARSE", `invalid decimal: "${text}"`);
    const [, sign, intPart, fracPart = ""] = m;
    const digits = BigInt(intPart + fracPart);
    return new _Decimal(sign ? -digits : digits, fracPart.length);
  }
  static of(digits, scale) {
    return new _Decimal(digits, scale);
  }
  static ONE = new _Decimal(1n, 0);
  isPositive() {
    return this.digits > 0n;
  }
  isZero() {
    return this.digits === 0n;
  }
  /** True when the value equals 1 regardless of scale ("1", "1.0", "1.0000"). */
  isOne() {
    return this.digits === 10n ** BigInt(this.scale);
  }
  toString() {
    const neg = this.digits < 0n;
    const abs = neg ? -this.digits : this.digits;
    const s = abs.toString().padStart(this.scale + 1, "0");
    const intPart = s.slice(0, s.length - this.scale);
    const frac = s.slice(s.length - this.scale);
    return `${neg ? "-" : ""}${intPart}${this.scale > 0 ? `.${frac}` : ""}`;
  }
};
function divRoundHalfAwayFromZero(n, d) {
  if (d <= 0n) throw new LedgerError("DECIMAL_PARSE", "divisor must be positive");
  const q = n / d;
  const r = n % d;
  const twice = (r < 0n ? -r : r) * 2n;
  if (twice >= d) return n < 0n ? q - 1n : q + 1n;
  return q;
}
function mulRound(minor, rate) {
  return divRoundHalfAwayFromZero(minor * rate.digits, 10n ** BigInt(rate.scale));
}
function convertMinorUnits(minor, rate, fromExp, toExp) {
  const up = 10n ** BigInt(Math.max(0, toExp - fromExp));
  const down = 10n ** BigInt(rate.scale + Math.max(0, fromExp - toExp));
  return divRoundHalfAwayFromZero(minor * rate.digits * up, down);
}

// src/money.ts
var DECIMAL_RE2 = /^(-)?(\d+)(?:\.(\d+))?$/;
function assertCurrency(currency) {
  if (!isCurrencyCode(currency)) {
    throw new LedgerError("CURRENCY_UNKNOWN", `unknown currency "${currency}"`, {
      known: Object.keys(CURRENCIES)
    });
  }
}
var Money = class _Money {
  minor;
  currency;
  constructor(minor, currency) {
    this.minor = minor;
    this.currency = currency;
    Object.freeze(this);
  }
  static of(minor, currency) {
    assertCurrency(currency);
    if (typeof minor === "number") {
      if (!Number.isSafeInteger(minor)) {
        throw new LedgerError(
          "MONEY_NOT_INTEGER",
          `minor units must be a safe integer, got ${minor}`
        );
      }
      return new _Money(BigInt(minor), currency);
    }
    return new _Money(minor, currency);
  }
  static zero(currency) {
    return _Money.of(0n, currency);
  }
  /**
   * Parse a decimal string ("1234.56", "-7", "0.5"). By default more decimal places than the
   * currency allows is an error; pass 'half-away-from-zero' to round instead.
   */
  static parse(text, currency, rounding = "reject") {
    assertCurrency(currency);
    const m = DECIMAL_RE2.exec(text.trim());
    if (!m) throw new LedgerError("MONEY_PARSE", `invalid decimal amount: "${text}"`);
    const [, sign, intPart, fracPart = ""] = m;
    const exp = exponentOf(currency);
    let frac = fracPart;
    let carry = 0n;
    if (frac.length > exp) {
      if (rounding === "reject") {
        throw new LedgerError(
          "MONEY_SCALE",
          `${currency} allows ${exp} decimal places, got ${frac.length}`,
          {
            currency,
            exponent: exp
          }
        );
      }
      const dropped = frac.slice(exp);
      frac = frac.slice(0, exp);
      if (dropped.charAt(0) >= "5") carry = 1n;
    }
    const abs = BigInt(intPart + frac.padEnd(exp, "0")) + carry;
    return new _Money(sign ? -abs : abs, currency);
  }
  assertSame(other, op) {
    if (other.currency !== this.currency) {
      throw new LedgerError(
        "CURRENCY_MISMATCH",
        `cannot ${op} ${this.currency} and ${other.currency}`,
        {
          left: this.currency,
          right: other.currency
        }
      );
    }
  }
  add(other) {
    this.assertSame(other, "add");
    return new _Money(this.minor + other.minor, this.currency);
  }
  subtract(other) {
    this.assertSame(other, "subtract");
    return new _Money(this.minor - other.minor, this.currency);
  }
  negate() {
    return new _Money(-this.minor, this.currency);
  }
  abs() {
    return this.minor < 0n ? this.negate() : this;
  }
  isZero() {
    return this.minor === 0n;
  }
  isNegative() {
    return this.minor < 0n;
  }
  isPositive() {
    return this.minor > 0n;
  }
  equals(other) {
    return this.currency === other.currency && this.minor === other.minor;
  }
  compare(other) {
    this.assertSame(other, "compare");
    return this.minor < other.minor ? -1 : this.minor > other.minor ? 1 : 0;
  }
  /** Multiply by an exact decimal (rate, quantity), rounding half away from zero. */
  multiply(factor) {
    const d = typeof factor === "string" ? Decimal.parse(factor) : factor;
    return new _Money(mulRound(this.minor, d), this.currency);
  }
  /**
   * Convert to another currency at an exact rate per whole unit (transaction → base), allowing for
   * different minor units (1.000 KWD at 11.96 = 11.96 AED).
   */
  convert(rate, to) {
    assertCurrency(to);
    const d = typeof rate === "string" ? Decimal.parse(rate) : rate;
    return new _Money(
      convertMinorUnits(this.minor, d, exponentOf(this.currency), exponentOf(to)),
      to
    );
  }
  /**
   * Split proportionally by non-negative integer ratios using the largest-remainder method,
   * so the parts always sum exactly to the whole and no part is off by more than one unit.
   */
  allocate(ratios) {
    if (ratios.length === 0) throw new LedgerError("ALLOCATE_INVALID", "ratios must not be empty");
    const rs = ratios.map((r) => {
      if (typeof r === "number") {
        if (!Number.isSafeInteger(r))
          throw new LedgerError("ALLOCATE_INVALID", `ratio ${r} is not an integer`);
        return BigInt(r);
      }
      return r;
    });
    if (rs.some((r) => r < 0n))
      throw new LedgerError("ALLOCATE_INVALID", "ratios must be non-negative");
    const total = rs.reduce((a, b) => a + b, 0n);
    if (total === 0n) throw new LedgerError("ALLOCATE_INVALID", "ratios must not all be zero");
    const neg = this.minor < 0n;
    const abs = neg ? -this.minor : this.minor;
    const shares = rs.map((r) => abs * r / total);
    const order = rs.map((r, i) => ({ i, rem: abs * r % total })).sort((a, b) => b.rem > a.rem ? 1 : b.rem < a.rem ? -1 : a.i - b.i);
    let left = abs - shares.reduce((a, b) => a + b, 0n);
    for (const { i } of order) {
      if (left === 0n) break;
      shares[i] = shares[i] + 1n;
      left -= 1n;
    }
    return shares.map((s) => new _Money(neg ? -s : s, this.currency));
  }
  /** "-1234.56" — decimal string with the currency's exponent, no grouping. */
  toDecimalString() {
    const exp = exponentOf(this.currency);
    const neg = this.minor < 0n;
    const abs = neg ? -this.minor : this.minor;
    const s = abs.toString().padStart(exp + 1, "0");
    const intPart = s.slice(0, s.length - exp);
    const frac = s.slice(s.length - exp);
    return `${neg ? "-" : ""}${intPart}${exp > 0 ? `.${frac}` : ""}`;
  }
  /** Wire format: { amount: "1234.56", currency: "AED" } (document 02, API conventions). */
  toJSON() {
    return { amount: this.toDecimalString(), currency: this.currency };
  }
  toString() {
    return `${this.currency} ${this.toDecimalString()}`;
  }
  static sum(items, currency) {
    return items.reduce((acc, m) => acc.add(m), _Money.zero(currency));
  }
};

// src/posting.ts
var SOURCE_TYPES = [
  "manual",
  "invoice",
  "credit_note",
  "receipt",
  "sales_receipt",
  "refund",
  "bill",
  "debit_note",
  "vendor_payment",
  "expense",
  "transfer",
  "bank",
  "opening",
  "year_end",
  "fx_reval",
  "fx_realised",
  "reversal",
  "vat_return",
  "vat_payment",
  "write_off",
  "depreciation",
  "asset_disposal",
  // inventory quantity adjustments and opening quantities (ADR-0015)
  "inventory_adjustment",
  // a QuickBooks Online transaction with no Ledger document type (migration 1500, document 12)
  "qbo_journal"
];
function trackingOf(source) {
  if (!source) return {};
  return {
    ...source.classId ? { classId: source.classId } : {},
    ...source.locationId ? { locationId: source.locationId } : {},
    ...source.projectId ? { projectId: source.projectId } : {}
  };
}
function lineTracking(doc, line) {
  return trackingOf({
    ...doc?.locationId ? { locationId: doc.locationId } : {},
    ...line?.classId ?? doc?.classId ? { classId: line?.classId ?? doc?.classId } : {},
    ...line?.projectId ?? doc?.projectId ? { projectId: line?.projectId ?? doc?.projectId } : {}
  });
}
var ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
function isValidIsoDate(value) {
  const m = ISO_DATE_RE.exec(value);
  if (!m) return false;
  const [, y, mo, d] = m;
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  return date.getUTCFullYear() === Number(y) && date.getUTCMonth() === Number(mo) - 1 && date.getUTCDate() === Number(d);
}
function preparePosting(intent, opts) {
  if (!isValidIsoDate(intent.date)) {
    throw new LedgerError("INVALID_DATE", `date must be YYYY-MM-DD, got "${intent.date}"`);
  }
  if (intent.lines.length < 2) {
    throw new LedgerError("TOO_FEW_LINES", "a journal entry needs at least two lines", {
      count: intent.lines.length
    });
  }
  const lines = [];
  let foreignLines = 0;
  intent.lines.forEach((input, index) => {
    const lineNo = index + 1;
    if (input.debit && input.credit) {
      throw new LedgerError("LINE_BOTH_SIDES", `line ${lineNo} has both a debit and a credit`, {
        lineNo
      });
    }
    const amount = input.debit ?? input.credit;
    if (!amount) {
      throw new LedgerError("LINE_NO_SIDE", `line ${lineNo} has neither a debit nor a credit`, {
        lineNo
      });
    }
    if (amount.isZero())
      throw new LedgerError("LINE_ZERO", `line ${lineNo} amount is zero`, { lineNo });
    if (amount.isNegative()) {
      throw new LedgerError(
        "LINE_NEGATIVE",
        `line ${lineNo} amount is negative; swap the side instead`,
        { lineNo }
      );
    }
    let rate;
    if (amount.currency === opts.baseCurrency) {
      if (input.fxRate !== void 0) {
        const given = Decimal.parse(input.fxRate);
        if (!given.isOne()) {
          throw new LedgerError(
            "FX_RATE_NOT_ONE",
            `line ${lineNo} is in the base currency; fxRate must be 1`,
            { lineNo }
          );
        }
      }
      rate = Decimal.ONE;
    } else {
      if (input.fxRate === void 0) {
        throw new LedgerError(
          "FX_RATE_REQUIRED",
          `line ${lineNo} is in ${amount.currency}; fxRate to ${opts.baseCurrency} is required`,
          { lineNo }
        );
      }
      rate = Decimal.parse(input.fxRate);
      if (!rate.isPositive()) {
        throw new LedgerError("FX_RATE_INVALID", `line ${lineNo} fxRate must be positive`, {
          lineNo
        });
      }
      foreignLines += 1;
    }
    const baseMinor = convertMinorUnits(
      amount.minor,
      rate,
      exponentOf(amount.currency),
      exponentOf(opts.baseCurrency)
    );
    if (baseMinor === 0n) {
      throw new LedgerError(
        "LINE_BASE_ZERO",
        `line ${lineNo} rounds to zero in ${opts.baseCurrency}`,
        { lineNo }
      );
    }
    const isDebit = input.debit !== void 0;
    lines.push({
      lineNo,
      accountId: input.accountId,
      currency: amount.currency,
      debitMinor: isDebit ? amount.minor : 0n,
      creditMinor: isDebit ? 0n : amount.minor,
      fxRate: rate.toString(),
      baseDebitMinor: isDebit ? baseMinor : 0n,
      baseCreditMinor: isDebit ? 0n : baseMinor,
      ...input.contactId !== void 0 ? { contactId: input.contactId } : {},
      ...input.taxCodeId !== void 0 ? { taxCodeId: input.taxCodeId } : {},
      ...input.description !== void 0 ? { description: input.description } : {},
      ...trackingOf(input),
      isRounding: false
    });
  });
  let totalDebit = lines.reduce((a, l) => a + l.baseDebitMinor, 0n);
  let totalCredit = lines.reduce((a, l) => a + l.baseCreditMinor, 0n);
  const diff = totalDebit - totalCredit;
  if (diff !== 0n) {
    const tolerance = BigInt(foreignLines);
    const absDiff = diff < 0n ? -diff : diff;
    if (foreignLines > 0 && absDiff <= tolerance && opts.roundingAccountId) {
      const creditSide = diff > 0n;
      lines.push({
        lineNo: lines.length + 1,
        accountId: opts.roundingAccountId,
        currency: opts.baseCurrency,
        debitMinor: creditSide ? 0n : absDiff,
        creditMinor: creditSide ? absDiff : 0n,
        fxRate: "1",
        baseDebitMinor: creditSide ? 0n : absDiff,
        baseCreditMinor: creditSide ? absDiff : 0n,
        description: "FX rounding",
        isRounding: true
      });
      totalDebit += creditSide ? 0n : absDiff;
      totalCredit += creditSide ? absDiff : 0n;
    } else {
      throw new LedgerError(
        "UNBALANCED",
        `entry is out of balance by ${diff} ${opts.baseCurrency} minor units`,
        {
          differenceMinor: diff,
          baseCurrency: opts.baseCurrency,
          totalBaseDebitMinor: totalDebit,
          totalBaseCreditMinor: totalCredit
        }
      );
    }
  }
  return {
    orgId: intent.orgId,
    date: intent.date,
    sourceType: intent.sourceType,
    ...intent.sourceId !== void 0 ? { sourceId: intent.sourceId } : {},
    ...intent.memo !== void 0 ? { memo: intent.memo } : {},
    isAdjusting: intent.isAdjusting ?? false,
    baseCurrency: opts.baseCurrency,
    lines,
    totalBaseDebitMinor: totalDebit,
    totalBaseCreditMinor: totalCredit
  };
}
function mirrorLines(lines) {
  return lines.map((l) => ({
    ...l,
    debitMinor: l.creditMinor,
    creditMinor: l.debitMinor,
    baseDebitMinor: l.baseCreditMinor,
    baseCreditMinor: l.baseDebitMinor
  }));
}

// src/fx.ts
var MAX_RATE_INT_DIGITS = 10;
var MAX_RATE_SCALE = 10;
function parseFxRate(text) {
  let d;
  try {
    d = Decimal.parse(text);
  } catch {
    throw new LedgerError("FX_RATE_INVALID", `"${text}" is not an exchange rate`);
  }
  if (!d.isPositive())
    throw new LedgerError("FX_RATE_INVALID", "an exchange rate must be more than zero");
  if (d.scale > MAX_RATE_SCALE)
    throw new LedgerError("FX_RATE_INVALID", "an exchange rate has at most 10 decimal places");
  const s = d.toString();
  const intPart = s.split(".")[0];
  if (intPart.replace(/^0+/, "").length > MAX_RATE_INT_DIGITS)
    throw new LedgerError("FX_RATE_INVALID", "that exchange rate is too large");
  return trimRate(s);
}
function trimRate(s) {
  return s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
}
function toBaseMinor(minor, rate, fromExp = 2, toExp = 2) {
  return convertMinorUnits(minor, Decimal.parse(rate), fromExp, toExp);
}
function fxFields(currency, fx) {
  if (!fx || currency === fx.baseCurrency) return {};
  return { fxRate: fx.rate };
}
function counterMoney(minor, currency, fx) {
  if (fx && fx.counterInBase && currency !== fx.baseCurrency)
    return {
      money: Money.of(minor, currency).convert(fx.rate, fx.baseCurrency),
      fields: {}
    };
  return { money: Money.of(minor, currency), fields: fxFields(currency, fx) };
}
function settlementBase(item, amountMinor) {
  if (amountMinor <= 0n)
    throw new LedgerError("ALLOCATE_INVALID", "an allocation must be more than zero");
  const left = item.totalMinor - item.settledMinor;
  if (amountMinor > left)
    throw new LedgerError("ALLOCATE_INVALID", "the allocation is more than is open on the item");
  if (amountMinor === left) return item.baseTotalMinor - item.settledBaseMinor;
  return toBaseMinor(amountMinor, item.rate, item.fromExp, item.toExp);
}
function realisedFx(side, sourceBase, targetBase) {
  const diff = sourceBase - targetBase;
  if (side === "receivable") return { controlDebitMinor: diff, gainMinor: diff };
  return { controlDebitMinor: -diff, gainMinor: -diff };
}
function buildRealisedFxPosting(input) {
  const { controlDebitMinor } = realisedFx(input.side, input.sourceBase, input.targetBase);
  if (controlDebitMinor === 0n) return null;
  const amount = Money.of(
    controlDebitMinor < 0n ? -controlDebitMinor : controlDebitMinor,
    input.baseCurrency
  );
  const controlDebit = controlDebitMinor > 0n;
  return {
    orgId: input.orgId,
    date: input.date,
    sourceType: "fx_realised",
    ...input.allocationId ? { sourceId: input.allocationId } : {},
    ...input.memo ? { memo: input.memo } : {},
    lines: [
      {
        accountId: input.controlAccountId,
        ...controlDebit ? { debit: amount } : { credit: amount },
        contactId: input.contactId,
        description: "Realised exchange gain or loss"
      },
      {
        accountId: input.fxAccountId,
        ...controlDebit ? { credit: amount } : { debit: amount },
        contactId: input.contactId,
        description: controlDebit ? "Exchange gain" : "Exchange loss"
      }
    ]
  };
}
function computeRevaluation(balances, rate, exponents = { foreign: 2, base: 2 }) {
  parseFxRate(rate);
  const lines = balances.map((b) => {
    const revaluedMinor = toBaseMinor(b.foreignMinor, rate, exponents.foreign, exponents.base);
    return { ...b, revaluedMinor, adjustmentMinor: revaluedMinor - b.baseMinor };
  });
  const gainMinor = lines.reduce((s, l) => s + l.adjustmentMinor, 0n);
  return { lines, gainMinor };
}
function buildRevaluationPosting(input) {
  const money = (m) => Money.of(m < 0n ? -m : m, input.baseCurrency);
  const description = `Revalue ${input.currency} at ${input.rate}`;
  const lines = [];
  let net = 0n;
  for (const l of input.lines) {
    if (l.adjustmentMinor === 0n) continue;
    net += l.adjustmentMinor;
    lines.push({
      accountId: l.accountId,
      ...l.adjustmentMinor > 0n ? { debit: money(l.adjustmentMinor) } : { credit: money(l.adjustmentMinor) },
      ...l.contactId ? { contactId: l.contactId } : {},
      description
    });
  }
  if (lines.length === 0) return null;
  if (net !== 0n)
    lines.push({
      accountId: input.fxAccountId,
      ...net > 0n ? { credit: money(net) } : { debit: money(net) },
      description: net > 0n ? "Unrealised exchange gain" : "Unrealised exchange loss"
    });
  if (lines.length < 2) return null;
  return {
    orgId: input.orgId,
    date: input.date,
    sourceType: "fx_reval",
    ...input.revaluationId ? { sourceId: input.revaluationId } : {},
    memo: input.memo ?? description,
    lines
  };
}

// src/sales.ts
function computeSalesLine(input, amountsAre) {
  const qty = Decimal.parse(input.quantity);
  if (!qty.isPositive() || qty.scale > 6) {
    throw new LedgerError(
      "SALES_LINE_QUANTITY",
      "quantity must be positive with at most 6 decimals",
      {
        quantity: input.quantity
      }
    );
  }
  if (input.unitPriceMinor < 0n) {
    throw new LedgerError("SALES_LINE_PRICE", "rate must not be negative");
  }
  if (!Number.isInteger(input.rateBp) || input.rateBp < 0 || input.rateBp > 1e4) {
    throw new LedgerError("SALES_TAX_RATE", "VAT rate must be 0\u2013100%", { rateBp: input.rateBp });
  }
  const amount = mulRound(input.unitPriceMinor, qty);
  const bp = amountsAre === "out_of_scope" ? 0n : BigInt(input.rateBp);
  if (amountsAre === "inclusive") {
    const tax2 = divRoundHalfAwayFromZero(amount * bp, 10000n + bp);
    return { netMinor: amount - tax2, taxMinor: tax2, grossMinor: amount };
  }
  const tax = divRoundHalfAwayFromZero(amount * bp, 10000n);
  return { netMinor: amount, taxMinor: tax, grossMinor: amount + tax };
}
function computeSalesTotals(lines, amountsAre) {
  const out = lines.map((l) => computeSalesLine(l, amountsAre));
  const subtotalMinor = out.reduce((s, l) => s + l.netMinor, 0n);
  const taxMinor = out.reduce((s, l) => s + l.taxMinor, 0n);
  return { lines: out, subtotalMinor, taxMinor, totalMinor: subtotalMinor + taxMinor };
}
function buildSalesPosting(input) {
  const money = (minor) => Money.of(minor, input.currency);
  const rate = fxFields(input.currency, input.fx);
  const salesSide = input.kind === "credit_note" ? "debit" : "credit";
  const counterSide = salesSide === "credit" ? "debit" : "credit";
  const income = [];
  const vatByCode = /* @__PURE__ */ new Map();
  let gross = 0n;
  for (const l of input.lines) {
    gross += l.netMinor + l.taxMinor;
    if (l.netMinor > 0n)
      income.push({
        accountId: l.accountId,
        [salesSide]: money(l.netMinor),
        ...rate,
        contactId: input.contactId,
        taxCodeId: l.taxCodeId,
        ...l.description ? { description: l.description } : {},
        ...lineTracking(input.tracking, l)
      });
    if (l.taxMinor > 0n)
      vatByCode.set(l.taxCodeId, (vatByCode.get(l.taxCodeId) ?? 0n) + l.taxMinor);
  }
  if (gross <= 0n)
    throw new LedgerError("SALES_TOTAL_ZERO", "the document total must be more than zero");
  const counterAccount = input.kind === "sales_receipt" ? input.depositAccountId : input.receivableAccountId;
  if (!counterAccount)
    throw new LedgerError("SALES_TOTAL_ZERO", "a sales receipt needs a deposit account");
  const vat = [...vatByCode].map(([taxCodeId, minor]) => ({
    accountId: input.vatOutputAccountId,
    [salesSide]: money(minor),
    ...rate,
    contactId: input.contactId,
    taxCodeId,
    description: "Output VAT",
    ...lineTracking(input.tracking)
  }));
  const counter = input.kind === "sales_receipt" ? counterMoney(gross, input.currency, input.fx) : { money: money(gross), fields: rate };
  return {
    orgId: input.orgId,
    date: input.date,
    sourceType: input.kind,
    sourceId: input.documentId,
    ...input.memo ? { memo: input.memo } : {},
    lines: [
      {
        accountId: counterAccount,
        [counterSide]: counter.money,
        ...counter.fields,
        contactId: input.contactId,
        ...lineTracking(input.tracking)
      },
      ...income,
      ...vat
    ]
  };
}
function buildReceiptPosting(input) {
  const amount = Money.of(input.amountMinor, input.currency);
  const deposit = counterMoney(input.amountMinor, input.currency, input.fx);
  return {
    orgId: input.orgId,
    date: input.date,
    sourceType: "receipt",
    sourceId: input.paymentId,
    ...input.memo ? { memo: input.memo } : {},
    lines: [
      {
        accountId: input.depositAccountId,
        debit: deposit.money,
        ...deposit.fields,
        contactId: input.contactId
      },
      {
        accountId: input.receivableAccountId,
        credit: amount,
        ...fxFields(input.currency, input.fx),
        contactId: input.contactId
      }
    ]
  };
}

// src/purchases.ts
function computePurchaseLine(input, amountsAre) {
  if (!input.selfAssessed) {
    const l2 = computeSalesLine(input, amountsAre);
    return { ...l2, selfAssessedTaxMinor: 0n };
  }
  const l = computeSalesLine({ ...input, rateBp: 0 }, "exclusive");
  const bp = amountsAre === "out_of_scope" ? 0n : BigInt(input.rateBp);
  const sa = divRoundHalfAwayFromZero(l.netMinor * bp, 10000n);
  computeSalesLine({ ...input, unitPriceMinor: 0n }, "exclusive");
  return { netMinor: l.netMinor, taxMinor: 0n, grossMinor: l.netMinor, selfAssessedTaxMinor: sa };
}
function computePurchaseTotals(lines, amountsAre) {
  const out = lines.map((l) => computePurchaseLine(l, amountsAre));
  const subtotalMinor = out.reduce((s, l) => s + l.netMinor, 0n);
  const taxMinor = out.reduce((s, l) => s + l.taxMinor, 0n);
  return {
    lines: out,
    subtotalMinor,
    taxMinor,
    totalMinor: subtotalMinor + taxMinor,
    selfAssessedTaxMinor: out.reduce((s, l) => s + l.selfAssessedTaxMinor, 0n)
  };
}
function splitRecoverable(l) {
  if (l.recoverableBp >= 1e4 || l.taxMinor === 0n)
    return { netMinor: l.netMinor, taxMinor: l.taxMinor };
  if (l.recoverableBp <= 0) return { netMinor: 0n, taxMinor: 0n };
  const net = divRoundHalfAwayFromZero(l.netMinor * BigInt(l.recoverableBp), 10000n);
  const tax = divRoundHalfAwayFromZero(net * BigInt(l.rateBp), 10000n);
  return { netMinor: net, taxMinor: tax > l.taxMinor ? l.taxMinor : tax };
}
function buildPurchasePosting(input) {
  if (!input.contactId && input.kind !== "expense")
    throw new LedgerError("CONTACT_UNKNOWN", "a bill or vendor credit names its vendor");
  const payee = input.contactId ? { contactId: input.contactId } : {};
  const money = (minor) => Money.of(minor, input.currency);
  const rate = fxFields(input.currency, input.fx);
  const costSide = input.kind === "debit_note" ? "credit" : "debit";
  const counterSide = costSide === "debit" ? "credit" : "debit";
  const cost = [];
  const inputVat = /* @__PURE__ */ new Map();
  const outputVat = /* @__PURE__ */ new Map();
  const add = (m, k, v) => {
    if (v > 0n) m.set(k, (m.get(k) ?? 0n) + v);
  };
  const costLine = (accountId, minor, taxCodeId, description, line) => {
    if (minor > 0n)
      cost.push({
        accountId,
        [costSide]: money(minor),
        ...rate,
        ...payee,
        taxCodeId,
        ...description ? { description } : {},
        ...lineTracking(input.tracking, line)
      });
  };
  let gross = 0n;
  for (const l of input.lines) {
    if (l.netMinor < 0n || l.taxMinor < 0n || l.selfAssessedTaxMinor < 0n)
      throw new LedgerError("PURCHASE_LINE", "purchase line amounts must not be negative");
    if (!Number.isInteger(l.recoverableBp) || l.recoverableBp < 0 || l.recoverableBp > 1e4)
      throw new LedgerError("PURCHASE_RECOVERY", "recoverable share must be 0\u2013100%");
    gross += l.netMinor + l.taxMinor;
    if (l.selfAssessedTaxMinor > 0n) {
      if (l.taxMinor > 0n || l.recoverableBp !== 1e4)
        throw new LedgerError(
          "PURCHASE_RECOVERY",
          "reverse charge and import VAT is recovered in full on this version"
        );
      costLine(l.accountId, l.netMinor, l.taxCodeId, l.description, l);
      add(inputVat, l.taxCodeId, l.selfAssessedTaxMinor);
      add(outputVat, l.taxCodeId, l.selfAssessedTaxMinor);
      continue;
    }
    const rec = splitRecoverable(l);
    const rest = l.netMinor - rec.netMinor + (l.taxMinor - rec.taxMinor);
    if (rest > 0n && !input.blockedTaxCodeId)
      throw new LedgerError(
        "PURCHASE_RECOVERY",
        "a blocked or partly recoverable line needs the blocked VAT code"
      );
    costLine(l.accountId, rec.netMinor, l.taxCodeId, l.description, l);
    add(inputVat, l.taxCodeId, rec.taxMinor);
    if (rest > 0n)
      costLine(
        l.accountId,
        rest,
        input.blockedTaxCodeId,
        rec.netMinor > 0n ? `${l.description ? `${l.description} \xB7 ` : ""}non-recoverable share` : l.description,
        l
      );
  }
  if (gross <= 0n)
    throw new LedgerError("SALES_TOTAL_ZERO", "the document total must be more than zero");
  const counterAccount = input.kind === "expense" ? input.paymentAccountId : input.payableAccountId;
  if (!counterAccount)
    throw new LedgerError("PURCHASE_LINE", "an expense needs the account it was paid from");
  const counter = input.kind === "expense" ? counterMoney(gross, input.currency, input.fx) : { money: money(gross), fields: rate };
  const vat = (m, accountId, side, text) => [...m].map(([taxCodeId, minor]) => ({
    accountId,
    [side]: money(minor),
    ...rate,
    ...payee,
    taxCodeId,
    description: text,
    ...lineTracking(input.tracking)
  }));
  return {
    orgId: input.orgId,
    date: input.date,
    sourceType: input.kind,
    sourceId: input.documentId,
    ...input.memo ? { memo: input.memo } : {},
    lines: [
      {
        accountId: counterAccount,
        [counterSide]: counter.money,
        ...counter.fields,
        ...payee,
        ...lineTracking(input.tracking)
      },
      ...cost,
      ...vat(inputVat, input.vatInputAccountId, costSide, "Input VAT"),
      ...vat(outputVat, input.vatOutputAccountId, counterSide, "Reverse charge output VAT")
    ]
  };
}
function buildVendorPaymentPosting(input) {
  const amount = Money.of(input.amountMinor, input.currency);
  const paid = counterMoney(input.amountMinor, input.currency, input.fx);
  return {
    orgId: input.orgId,
    date: input.date,
    sourceType: "vendor_payment",
    sourceId: input.paymentId,
    ...input.memo ? { memo: input.memo } : {},
    lines: [
      {
        accountId: input.payableAccountId,
        debit: amount,
        ...fxFields(input.currency, input.fx),
        contactId: input.contactId
      },
      {
        accountId: input.paymentAccountId,
        credit: paid.money,
        ...paid.fields,
        contactId: input.contactId
      }
    ]
  };
}

// src/vat.ts
function vatSettlementLines(v) {
  if (v.output.currency !== v.input.currency) {
    throw new LedgerError("CURRENCY_MISMATCH", "output and input VAT must be in one currency");
  }
  const lines = [];
  const side = (accountId, amount, debitWhenPositive, description) => {
    if (amount.minor === 0n) return;
    const positive = amount.minor > 0n;
    const key = positive === debitWhenPositive ? "debit" : "credit";
    lines.push({ accountId, [key]: amount.abs(), ...description ? { description } : {} });
  };
  side(v.outputAccountId, v.output, true, v.description?.output);
  side(v.inputAccountId, v.input, false, v.description?.input);
  side(v.controlAccountId, v.output.subtract(v.input), false, v.description?.control);
  return lines;
}

// src/recurrence.ts
var DAY_MS = 864e5;
var toDays = (iso2) => {
  const [y, m, d] = iso2.split("-").map(Number);
  return Date.UTC(y, m - 1, d) / DAY_MS;
};
var fromDays = (days) => new Date(days * DAY_MS).toISOString().slice(0, 10);
var addDays = (iso2, n) => fromDays(toDays(iso2) + n);
var weekdayOf = (iso2) => new Date(toDays(iso2) * DAY_MS).getUTCDay();
var daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();
function dayInMonth(monthIndex, dayOfMonth) {
  const year = Math.floor(monthIndex / 12);
  const month = monthIndex % 12 + 1;
  const last = daysInMonth(year, month);
  const day = dayOfMonth === -1 ? last : Math.min(dayOfMonth, last);
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}
var monthIndexOf = (iso2) => {
  const [y, m] = iso2.split("-").map(Number);
  return y * 12 + m - 1;
};
function validateSchedule(s) {
  const bad = (message) => {
    throw new LedgerError("SCHEDULE_INVALID", message);
  };
  if (!isValidIsoDate(s.startDate)) bad("enter a start date");
  if (!Number.isInteger(s.every) || s.every < 1 || s.every > 999) bad("repeat every 1 to 999");
  if (s.frequency === "weekly" && !isWeekday(s.dayOfWeek)) bad("choose a day of the week");
  if ((s.frequency === "monthly" || s.frequency === "yearly") && !(s.dayOfMonth === -1 || isInt(s.dayOfMonth) && s.dayOfMonth >= 1 && s.dayOfMonth <= 31))
    bad("choose a day of the month");
  if (s.frequency === "yearly" && !(isInt(s.monthOfYear) && s.monthOfYear >= 1 && s.monthOfYear <= 12))
    bad("choose a month");
  const end = s.end ?? { type: "none" };
  if (end.type === "after" && !(isInt(end.count) && end.count >= 1)) bad("end after 1 or more");
  if (end.type === "on" && !(isValidIsoDate(end.date) && end.date >= s.startDate))
    bad("the end date must be on or after the start date");
}
var isInt = (n) => typeof n === "number" && Number.isInteger(n);
var isWeekday = (n) => isInt(n) && n >= 0 && n <= 6;
function occurrenceAt(s, k) {
  const start = s.startDate;
  switch (s.frequency) {
    case "daily":
      return addDays(start, k * s.every);
    case "weekly": {
      const first = addDays(start, (s.dayOfWeek - weekdayOf(start) + 7) % 7);
      return addDays(first, 7 * s.every * k);
    }
    case "monthly": {
      let anchor = monthIndexOf(start);
      if (dayInMonth(anchor, s.dayOfMonth) < start) anchor += 1;
      return dayInMonth(anchor + k * s.every, s.dayOfMonth);
    }
    case "yearly": {
      const [y] = start.split("-").map(Number);
      let year = y;
      const at = (yr) => dayInMonth(yr * 12 + s.monthOfYear - 1, s.dayOfMonth);
      if (at(year) < start) year += 1;
      return at(year + k * s.every);
    }
  }
}
function withinEnd(s, k, date) {
  const end = s.end ?? { type: "none" };
  if (end.type === "after") return k < end.count;
  if (end.type === "on") return date <= end.date;
  return true;
}
function firstOccurrenceOnOrAfter(s, date) {
  let k = 0;
  if (date > s.startDate) {
    const first = occurrenceAt(s, 0);
    if (first < date) {
      switch (s.frequency) {
        case "daily":
          k = Math.floor((toDays(date) - toDays(first)) / s.every);
          break;
        case "weekly":
          k = Math.floor((toDays(date) - toDays(first)) / (7 * s.every));
          break;
        case "monthly":
          k = Math.floor((monthIndexOf(date) - monthIndexOf(first)) / s.every) - 1;
          break;
        case "yearly":
          k = Math.floor((monthIndexOf(date) - monthIndexOf(first)) / (12 * s.every)) - 1;
          break;
      }
      k = Math.max(0, k);
    }
  }
  let occ = occurrenceAt(s, k);
  while (occ < date) {
    k += 1;
    occ = occurrenceAt(s, k);
  }
  return withinEnd(s, k, occ) ? { index: k, date: occ } : null;
}
function nextOccurrenceAfter(s, date) {
  return firstOccurrenceOnOrAfter(s, addDays(date, 1))?.date ?? null;
}
var WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
var MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];
var ordinal = (n) => {
  const t = n % 100;
  if (t >= 11 && t <= 13) return `${n}th`;
  return `${n}${["th", "st", "nd", "rd"][n % 10] ?? "th"}`;
};
function describeSchedule(s) {
  const n = s.every;
  const unit = { daily: "day", weekly: "week", monthly: "month", yearly: "year" }[s.frequency];
  const every = n === 1 ? `Every ${unit}` : `Every ${n} ${unit}s`;
  switch (s.frequency) {
    case "daily":
      return every;
    case "weekly":
      return `${every} on ${WEEKDAYS[s.dayOfWeek ?? 0]}`;
    case "monthly":
      return `${every} on the ${s.dayOfMonth === -1 ? "last day" : ordinal(s.dayOfMonth ?? 1)}`;
    case "yearly": {
      const month = MONTHS[(s.monthOfYear ?? 1) - 1];
      return `${every} on ${s.dayOfMonth === -1 ? `the last day of ${month}` : `${s.dayOfMonth ?? 1} ${month}`}`;
    }
  }
}

// src/migration.ts
function parseCsvText(text) {
  const src = text.replace(/^\uFEFF/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(cell);
      cell = "";
      if (row.some((c) => c.trim() !== "")) rows.push(row);
      row = [];
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim() !== "")) rows.push(row);
  return rows;
}
var AMOUNT_RE = /^(\d{1,3}(?:,\d{3})+|\d{1,2}(?:,\d{2})+,\d{3}|\d+)(?:\.(\d+))?$/;
function parseReportAmount(text, exp = 2) {
  let s = text.trim().replace(/\u00a0/g, " ");
  if (s === "" || s === "-" || s === "\u2014" || s === "\u2013") return null;
  let neg = false;
  if (/^\(.*\)$/.test(s)) {
    neg = true;
    s = s.slice(1, -1).trim();
  }
  if (s.startsWith("-")) {
    neg = !neg;
    s = s.slice(1).trim();
  } else if (s.endsWith("-")) {
    neg = !neg;
    s = s.slice(0, -1).trim();
  }
  s = s.replace(/^(?:[A-Z]{3}|₹|Rs\.?)\s*/, "").replace(/\s*(?:[A-Z]{3}|₹)$/, "");
  if (s.startsWith("-")) {
    neg = !neg;
    s = s.slice(1).trim();
  }
  const m = AMOUNT_RE.exec(s);
  if (!m) throw new LedgerError("MONEY_PARSE", `"${text.trim()}" is not an amount`);
  const frac = m[2] ?? "";
  if (frac.length > exp)
    throw new LedgerError("MONEY_SCALE", `"${text.trim()}" has more than ${exp} decimal places`);
  const minor = BigInt(m[1].replace(/,/g, "") + frac.padEnd(exp, "0"));
  return neg && minor !== 0n ? -minor : minor;
}
function tryReportAmount(text, exp = 2) {
  if (text === void 0) return null;
  try {
    return parseReportAmount(text, exp);
  } catch {
    return null;
  }
}
function formatMinor(minor, exp = 2) {
  const neg = minor < 0n;
  const s = (neg ? -minor : minor).toString().padStart(exp + 1, "0");
  const int = s.slice(0, s.length - exp).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${neg ? "-" : ""}${int}${exp ? `.${s.slice(s.length - exp)}` : ""}`;
}
var MONTHS2 = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec"
];
var iso = (y, m, d) => {
  if (m < 1 || m > 12 || d < 1) return null;
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  if (d > days) return null;
  return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};
var monthOf = (word) => {
  const i = MONTHS2.indexOf(word.slice(0, 3).toLowerCase());
  return i < 0 ? 0 : i + 1;
};
function parseReportDate(text) {
  const s = text.trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/.exec(s);
  if (m) return iso(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})$/.exec(s);
  if (m) {
    const y = m[3].length === 2 ? 2e3 + Number(m[3]) : Number(m[3]);
    return iso(y, Number(m[2]), Number(m[1]));
  }
  m = /^(\d{1,2})[\s-]([A-Za-z]{3,9})[\s,-]+(\d{4})$/.exec(s);
  if (m && monthOf(m[2])) return iso(Number(m[3]), monthOf(m[2]), Number(m[1]));
  m = /^([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(s);
  if (m && monthOf(m[1])) return iso(Number(m[3]), monthOf(m[1]), Number(m[2]));
  return null;
}
function asOfFrom(grid, before) {
  for (const row of grid.slice(0, Math.max(before, 0))) {
    for (const cell of [...row, row.join(",")]) {
      const m = /as (?:of|at)\s+(.+)$/i.exec(cell.trim());
      if (m) {
        const d = parseReportDate(m[1].trim());
        if (d) return d;
      }
    }
  }
  return null;
}
function reportPeriodFrom(grid) {
  for (const row of grid.slice(0, 8)) {
    for (const raw of row) {
      const cell = raw.trim();
      const asOf = /as (?:of|at)\s+(.+)$/i.exec(cell);
      if (asOf) {
        const d = parseReportDate(asOf[1].trim());
        if (d) return { from: null, to: d };
      }
      const range = cell.split(/\s+(?:-|–|to)\s+/i);
      if (range.length === 2) {
        const to = parseReportDate(range[1]);
        let from = parseReportDate(range[0]);
        if (!from && to) from = parseReportDate(`${range[0]} ${to.slice(0, 4)}`);
        if (from && to) return { from, to };
      }
      const month = /^([A-Za-z]{3,9})\s+(\d{4})$/.exec(cell);
      if (month && monthOf(month[1])) {
        const y = Number(month[2]);
        const mo = monthOf(month[1]);
        return { from: iso(y, mo, 1), to: iso(y, mo, new Date(Date.UTC(y, mo, 0)).getUTCDate()) };
      }
    }
  }
  return { from: null, to: null };
}
var normLabel = (s) => s.trim().toLowerCase().replace(/\s+/g, " ").replace(/[*:]+$/, "");
var findHeader = (grid, test) => grid.findIndex((row) => test(row.map(normLabel)));
var isTotalLabel = (label) => /^total\b/i.test(label.trim());
function parseQboTrialBalance(grid, exp = 2) {
  const h = findHeader(grid, (c) => c.includes("debit") && c.includes("credit"));
  const issues = [];
  const out = {
    asOf: null,
    lines: [],
    totalDebitMinor: 0n,
    totalCreditMinor: 0n,
    fileTotal: null,
    issues
  };
  if (h < 0) {
    issues.push({ row: 0, message: "no header row with Debit and Credit columns" });
    return out;
  }
  out.asOf = asOfFrom(grid, h);
  const head = grid[h].map(normLabel);
  const dc = head.indexOf("debit");
  const cc = head.indexOf("credit");
  for (let i = h + 1; i < grid.length; i++) {
    const row = grid[i];
    if (/\b(accrual|cash) basis\b/i.test(row[0] ?? "")) break;
    const label = row.slice(0, Math.min(dc, cc)).map((x) => x.trim()).filter(Boolean).join(" ");
    let d;
    let c;
    try {
      d = parseReportAmount(row[dc] ?? "", exp);
      c = parseReportAmount(row[cc] ?? "", exp);
    } catch (e) {
      issues.push({ row: i + 1, message: e.message });
      continue;
    }
    if (d === null && c === null) continue;
    if (isTotalLabel(label)) {
      out.fileTotal = { debitMinor: d ?? 0n, creditMinor: c ?? 0n };
      break;
    }
    if (!label) {
      issues.push({ row: i + 1, message: "amount without an account name" });
      continue;
    }
    const net = (d ?? 0n) - (c ?? 0n);
    if (net === 0n) continue;
    const line = {
      row: i + 1,
      label,
      debitMinor: net > 0n ? net : 0n,
      creditMinor: net < 0n ? -net : 0n
    };
    out.lines.push(line);
    out.totalDebitMinor += line.debitMinor;
    out.totalCreditMinor += line.creditMinor;
  }
  if (!out.lines.length) issues.push({ row: 0, message: "the file has no account balances" });
  if (out.totalDebitMinor !== out.totalCreditMinor)
    issues.push({
      row: 0,
      message: `debits (${formatMinor(out.totalDebitMinor, exp)}) do not equal credits (${formatMinor(out.totalCreditMinor, exp)})`
    });
  if (out.fileTotal && (out.fileTotal.debitMinor !== out.totalDebitMinor || out.fileTotal.creditMinor !== out.totalCreditMinor) && out.fileTotal.debitMinor - out.fileTotal.creditMinor !== out.totalDebitMinor - out.totalCreditMinor)
    issues.push({ row: 0, message: "the TOTAL row does not equal the sum of the lines" });
  return out;
}
var CREDIT_TYPE = /credit|payment|cheque|check|refund|deposit|journal|supplier credit|vendor credit/i;
function parseQboAgeingDetail(grid, side, exp = 2) {
  const h = findHeader(
    grid,
    (c) => c.includes("date") && c.some((x) => /open balance|balance/.test(x))
  );
  const issues = [];
  const out = { asOf: null, items: [], totalMinor: 0n, issues };
  if (h < 0) {
    issues.push({ row: 0, message: "no header row with Date and Open balance columns" });
    return out;
  }
  out.asOf = asOfFrom(grid, h);
  const head = grid[h].map(normLabel);
  const col = (...names) => head.findIndex((x) => names.some((n) => n.test(x)));
  const iDate = head.indexOf("date");
  const iType = col(/^transaction type$/, /^type$/);
  const iNum = col(/^no\.?$/, /^num$/, /^number$/, /^invoice no/, /^bill no/, /^ref/);
  const iName = side === "receivable" ? col(/^customer/, /^name$/, /^client/) : col(/^supplier/, /^vendor/, /^payee/, /^name$/);
  const iDue = col(/^due date$/, /^due$/);
  const iAmount = col(/^amount$/, /^total$/, /^original amount$/);
  const iOpen = col(/^open balance$/, /^balance$/, /^open$/);
  const iMemo = col(/^memo/, /^description$/);
  if (iName < 0)
    issues.push({
      row: h + 1,
      message: `no ${side === "receivable" ? "Customer" : "Supplier"} column`
    });
  if (iOpen < 0) issues.push({ row: h + 1, message: "no Open balance column" });
  if (iName < 0 || iOpen < 0) return out;
  for (let i = h + 1; i < grid.length; i++) {
    const row = grid[i];
    const date = parseReportDate(row[iDate] ?? "");
    if (!date) continue;
    const cell = (j) => j >= 0 ? (row[j] ?? "").trim() : "";
    let open;
    let amount;
    try {
      open = parseReportAmount(cell(iOpen), exp);
      amount = iAmount >= 0 ? parseReportAmount(cell(iAmount), exp) : open;
    } catch (e) {
      issues.push({ row: i + 1, message: e.message });
      continue;
    }
    if (open === null || open === 0n) continue;
    const type = cell(iType) || (side === "receivable" ? "Invoice" : "Bill");
    const credit = open < 0n || CREDIT_TYPE.test(type);
    const name = cell(iName);
    const dueText = cell(iDue);
    const due = dueText ? parseReportDate(dueText) : null;
    const item = {
      row: i + 1,
      kind: credit ? "credit" : "document",
      type,
      date,
      dueDate: due,
      number: cell(iNum),
      name,
      amountMinor: (amount ?? open) < 0n ? -(amount ?? open) : amount ?? open,
      openMinor: open < 0n ? -open : open,
      memo: cell(iMemo)
    };
    if (!name) issues.push({ row: i + 1, message: "no customer or supplier name" });
    else if (dueText && !due)
      issues.push({ row: i + 1, message: `due date "${dueText}" is not a date` });
    else if (item.openMinor > item.amountMinor)
      issues.push({ row: i + 1, message: "open balance is more than the amount" });
    else {
      out.items.push(item);
      out.totalMinor += credit ? -item.openMinor : item.openMinor;
    }
  }
  if (!out.items.length && !issues.length)
    issues.push({ row: 0, message: "the file has no open items" });
  return out;
}
function parseQboSummary(grid, exp = 2) {
  const issues = [];
  const lines = [];
  const h = findHeader(grid, (c) => c.slice(1).some((x) => x === "total" || /\d{4}/.test(x)));
  let amountCol = -1;
  if (h >= 0) {
    const head = grid[h].map(normLabel);
    amountCol = head.lastIndexOf("total");
    if (amountCol < 1) amountCol = head.findIndex((x, i) => i > 0 && x !== "");
  }
  for (let i = h + 1; i < grid.length; i++) {
    const row = grid[i];
    const label = (row[0] ?? "").trim();
    if (!label) continue;
    const col = amountCol >= 1 ? amountCol : row.findIndex((x, j) => j > 0 && tryReportAmount(x, exp) !== null);
    if (col < 1) continue;
    const text = row[col] ?? "";
    let v;
    try {
      v = parseReportAmount(text, exp);
    } catch (e) {
      issues.push({ row: i + 1, message: e.message });
      continue;
    }
    if (v === null) continue;
    lines.push({ row: i + 1, label, amountMinor: v });
  }
  if (!lines.length) issues.push({ row: 0, message: "the file has no amounts" });
  const period = reportPeriodFrom(grid);
  return { ...period, lines, issues };
}
var AGEING_BUCKET_KEYS = ["current", "1-30", "31-60", "61-90", "91+"];
function parseQboAgeingSummary(grid, exp = 2) {
  const issues = [];
  const rows = [];
  const h = findHeader(grid, (c) => c.includes("current") && c.includes("total"));
  if (h < 0) {
    issues.push({ row: 0, message: "no header row with Current and Total columns" });
    return { asOf: null, rows, issues };
  }
  const head = grid[h].map(normLabel);
  const at = (re) => head.findIndex((x) => re.test(x.replace(/\s+/g, "")));
  const cols = {
    current: head.indexOf("current"),
    "1-30": at(/^1-30/),
    "31-60": at(/^31-60/),
    "61-90": at(/^61-90/),
    "91+": at(/^(91|90|over90|>90)/)
  };
  const iTotal = head.lastIndexOf("total");
  for (let i = h + 1; i < grid.length; i++) {
    const row = grid[i];
    const name = (row[0] ?? "").trim();
    if (!name || isTotalLabel(name)) continue;
    try {
      const total = parseReportAmount(row[iTotal] ?? "", exp);
      if (total === null) continue;
      const buckets = Object.fromEntries(
        AGEING_BUCKET_KEYS.map((k) => [
          k,
          cols[k] >= 0 ? parseReportAmount(row[cols[k]] ?? "", exp) ?? 0n : 0n
        ])
      );
      rows.push({ row: i + 1, name, buckets, totalMinor: total });
    } catch (e) {
      issues.push({ row: i + 1, message: e.message });
    }
  }
  if (!rows.length && !issues.length) issues.push({ row: 0, message: "the file has no balances" });
  return { asOf: asOfFrom(grid, h), rows, issues };
}
var VAT201_BOXES = [
  "1a",
  "1b",
  "1c",
  "1d",
  "1e",
  "1f",
  "1g",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14"
];
function parseVat201(grid, exp = 2) {
  const issues = [];
  const boxes = [];
  const h = findHeader(
    grid,
    (c) => c.some((x) => /amount/.test(x)) && c.some((x) => /vat/.test(x))
  );
  const head = h >= 0 ? grid[h].map(normLabel) : [];
  const iVat = head.findIndex((x) => /vat/.test(x) && !/^box/.test(x) && x !== "vat return");
  const iAmount = head.findIndex((x, i) => /amount/.test(x) && i !== iVat);
  for (let i = h + 1; i < grid.length; i++) {
    const row = grid[i];
    let box = null;
    for (const cell of row.slice(0, 2)) {
      const m = /^\s*(?:box\s*)?(1[a-g]|1[0-4]|[2-9])\b/i.exec(cell);
      if (m) {
        box = m[1].toLowerCase();
        break;
      }
    }
    if (!box || !VAT201_BOXES.includes(box)) continue;
    if (boxes.some((b) => b.box === box)) {
      issues.push({ row: i + 1, message: `box ${box} appears twice` });
      continue;
    }
    try {
      let amount;
      let vat;
      if (iAmount >= 0 && iVat >= 0) {
        amount = parseReportAmount(row[iAmount] ?? "", exp);
        vat = parseReportAmount(row[iVat] ?? "", exp);
      } else {
        const nums = row.slice(1).map((x) => tryReportAmount(x, exp)).filter((x) => x !== null);
        [amount = null, vat = null] = ["12", "13", "14"].includes(box) ? [null, nums[nums.length - 1] ?? null] : nums;
      }
      boxes.push({ row: i + 1, box, amountMinor: amount ?? 0n, vatMinor: vat ?? 0n });
    } catch (e) {
      issues.push({ row: i + 1, message: e.message });
    }
  }
  if (!boxes.length) issues.push({ row: 0, message: "no VAT 201 boxes found (1a to 14)" });
  return { ...reportPeriodFrom(grid), boxes, issues };
}
function splitAccountLabel(label) {
  let s = label.trim().replace(/\s+/g, " ");
  let number = null;
  const lead = /^(\d[\d.-]*)\s*(?:·|-|–|\s)\s*(.+)$/.exec(s);
  const trail = /^(.+?)\s*\((\d[\d.-]*)\)$/.exec(s);
  if (lead) {
    number = lead[1];
    s = lead[2];
  } else if (trail) {
    number = trail[2];
    s = trail[1];
  }
  const path = s.split(":").map((p) => p.trim()).filter(Boolean);
  const last = path[path.length - 1] ?? s;
  const inner = /^(\d[\d.-]*)\s*(?:·|-|–|\s)\s*(.+)$/.exec(last);
  if (inner && path.length > 1) {
    number = inner[1];
    path[path.length - 1] = inner[2];
  }
  return { number, name: path[path.length - 1] ?? s, path };
}
function matchAccount(label, accounts, saved) {
  const fromSaved = saved?.get(normLabel(label));
  if (fromSaved && accounts.some((a) => a.id === fromSaved))
    return { accountId: fromSaved, how: "saved" };
  const l = splitAccountLabel(label);
  if (l.number) {
    const a = accounts.find((x) => x.code === l.number);
    if (a) return { accountId: a.id, how: "number" };
  }
  const path = normLabel(l.path.join(":"));
  const byPath = accounts.find((a) => normLabel(a.fullName) === path);
  if (byPath && l.path.length > 1) return { accountId: byPath.id, how: "path" };
  const named = accounts.filter((a) => normLabel(a.name) === normLabel(l.name));
  if (named.length === 1) return { accountId: named[0].id, how: "name" };
  if (byPath) return { accountId: byPath.id, how: "path" };
  return null;
}
function buildOpeningBalancePosting(input) {
  const net = /* @__PURE__ */ new Map();
  for (const l of input.lines) {
    if (l.debitMinor < 0n || l.creditMinor < 0n)
      throw new LedgerError("LINE_NEGATIVE", "opening balances are positive debits or credits");
    const prev = net.get(l.accountId);
    net.set(l.accountId, {
      minor: (prev?.minor ?? 0n) + l.debitMinor - l.creditMinor,
      ...prev?.description ?? l.description ? { description: prev?.description ?? l.description } : {}
    });
  }
  const lines = [];
  let balance = 0n;
  for (const [accountId, v] of net) {
    if (v.minor === 0n) continue;
    balance += v.minor;
    lines.push({
      accountId,
      ...v.minor > 0n ? { debit: Money.of(v.minor, input.currency) } : { credit: Money.of(-v.minor, input.currency) },
      ...v.description ? { description: v.description } : {}
    });
  }
  if (balance !== 0n) {
    const existing = lines.findIndex((l) => l.accountId === input.openingEquityAccountId);
    const own = existing >= 0 ? lines.splice(existing, 1)[0] : null;
    const ownMinor = own ? (own.debit?.minor ?? 0n) - (own.credit?.minor ?? 0n) : 0n;
    const obe = ownMinor - balance;
    if (obe !== 0n)
      lines.push({
        accountId: input.openingEquityAccountId,
        ...obe > 0n ? { debit: Money.of(obe, input.currency) } : { credit: Money.of(-obe, input.currency) },
        description: "Opening balance equity"
      });
  }
  if (lines.length < 2)
    throw new LedgerError("TOO_FEW_LINES", "an opening balance needs at least two balances");
  return {
    orgId: input.orgId,
    date: input.date,
    sourceType: "opening",
    ...input.sourceId ? { sourceId: input.sourceId } : {},
    memo: input.memo ?? "Opening balances",
    lines
  };
}
function buildOpeningItemPosting(input) {
  if (input.amountMinor <= 0n)
    throw new LedgerError("LINE_ZERO", "an open item needs a positive open balance");
  const amount = Money.of(input.amountMinor, input.currency);
  const controlDebit = input.side === "receivable" === (input.kind === "document");
  const control = {
    accountId: input.controlAccountId,
    contactId: input.contactId,
    description: input.description,
    ...controlDebit ? { debit: amount } : { credit: amount }
  };
  const equity = {
    accountId: input.openingEquityAccountId,
    contactId: input.contactId,
    description: input.description,
    ...controlDebit ? { credit: amount } : { debit: amount }
  };
  return {
    orgId: input.orgId,
    date: input.date,
    sourceType: "opening",
    ...input.sourceId ? { sourceId: input.sourceId } : {},
    memo: input.description,
    lines: [control, equity]
  };
}
function compareFigures(qbo, outss, order = []) {
  const keys = [.../* @__PURE__ */ new Set([...order, ...outss.keys(), ...qbo.keys()])];
  const out = [];
  for (const key of keys) {
    const q = qbo.get(key);
    const o = outss.get(key);
    if (!q && !o) continue;
    out.push({
      key,
      label: o?.label ?? q.label,
      qboMinor: q ? q.minor : null,
      outssMinor: o ? o.minor : null,
      differenceMinor: (o?.minor ?? 0n) - (q?.minor ?? 0n)
    });
  }
  return out;
}
var RUN_REPORTS = [
  "trial_balance",
  "profit_and_loss",
  "balance_sheet",
  "ar_ageing",
  "ap_ageing",
  "vat_201"
];
var AGEING_BUCKET_LABELS = {
  current: "Total current",
  "1-30": "Total 1\u201330 days past due",
  "31-60": "Total 31\u201360 days past due",
  "61-90": "Total 61\u201390 days past due",
  "91+": "Total 91 and over days past due"
};
var VAT_ONLY_BOXES = /* @__PURE__ */ new Set(["12", "13", "14"]);
var vatFigureKey = (box, part) => `box:${box}:${part}`;
function parseQboFigures(report, grid, exp = 2) {
  const s = (v) => v.toString();
  if (report === "trial_balance") {
    const tb = parseQboTrialBalance(grid, exp);
    return {
      lines: tb.lines.map((l) => ({ label: l.label, minor: s(l.debitMinor - l.creditMinor) })),
      issues: tb.issues,
      from: null,
      to: tb.asOf
    };
  }
  if (report === "profit_and_loss" || report === "balance_sheet") {
    const p = parseQboSummary(grid, exp);
    return {
      lines: p.lines.map((l) => ({ label: l.label, minor: s(l.amountMinor) })),
      issues: p.issues,
      from: p.from,
      to: p.to
    };
  }
  if (report === "vat_201") {
    const v = parseVat201(grid, exp);
    const lines2 = [];
    for (const b of v.boxes) {
      if (!VAT_ONLY_BOXES.has(b.box))
        lines2.push({
          key: vatFigureKey(b.box, "amount"),
          label: `Box ${b.box} \u2013 amount`,
          minor: s(b.amountMinor)
        });
      lines2.push({
        key: vatFigureKey(b.box, "vat"),
        label: `Box ${b.box} \u2013 VAT`,
        minor: s(b.vatMinor)
      });
    }
    return { lines: lines2, issues: v.issues, from: v.from, to: v.to };
  }
  const a = parseQboAgeingSummary(grid, exp);
  const totals = Object.fromEntries(AGEING_BUCKET_KEYS.map((k) => [k, 0n]));
  const lines = a.rows.map((r) => {
    for (const k of AGEING_BUCKET_KEYS) totals[k] += r.buckets[k];
    return { key: `contact:${normLabel(r.name)}`, label: r.name, minor: s(r.totalMinor) };
  });
  for (const k of AGEING_BUCKET_KEYS)
    lines.push({ key: `bucket:${k}`, label: AGEING_BUCKET_LABELS[k], minor: s(totals[k]) });
  return { lines, issues: a.issues, from: null, to: a.asOf };
}

// src/assets.ts
var MONTH_RE = /^(\d{4})-(\d{2})-01$/;
function monthOf2(date) {
  return `${date.slice(0, 7)}-01`;
}
function addMonths(month, n) {
  const [y, m] = month.split("-").map(Number);
  const idx = y * 12 + (m - 1) + n;
  return `${String(Math.floor(idx / 12)).padStart(4, "0")}-${String(idx % 12 + 1).padStart(2, "0")}-01`;
}
function monthsBetween(a, b) {
  const [ya, ma] = a.split("-").map(Number);
  const [yb, mb] = b.split("-").map(Number);
  return (yb - ya) * 12 + (mb - ma);
}
function monthEnd(month) {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month.slice(0, 7)}-${String(last).padStart(2, "0")}`;
}
function defaultDecliningRateBp(usefulLifeMonths) {
  return Math.min(1e4, Math.round(24e4 / usefulLifeMonths));
}
function validateAssetTerms(t) {
  const fail = (message) => {
    throw new LedgerError("ASSET_TERMS", message);
  };
  if (t.costMinor <= 0n) fail("cost must be more than zero");
  if (t.residualMinor < 0n) fail("residual value cannot be negative");
  if (t.residualMinor >= t.costMinor) fail("residual value must be less than the cost");
  if (!Number.isInteger(t.usefulLifeMonths) || t.usefulLifeMonths < 1 || t.usefulLifeMonths > 600)
    fail("useful life must be 1 to 600 months");
  if (!MONTH_RE.test(t.startMonth)) fail("depreciation start must be the first of a month");
  if (t.method === "declining_balance") {
    const r = t.decliningRateBp;
    if (r === void 0 || !Number.isInteger(r) || r < 1 || r > 1e4)
      fail("declining balance needs an annual rate of 0.01% to 100%");
  }
}
function monthlyCharge(t, index, accumulatedBefore) {
  if (index < 0 || index >= t.usefulLifeMonths) return 0n;
  const depreciable = t.costMinor - t.residualMinor;
  const remaining = depreciable - accumulatedBefore;
  if (remaining <= 0n) return 0n;
  const monthsLeft = BigInt(t.usefulLifeMonths - index);
  if (monthsLeft === 1n) return remaining;
  if (t.method === "straight_line") {
    const charge2 = divRoundHalfAwayFromZero(remaining, monthsLeft);
    return charge2 > remaining ? remaining : charge2;
  }
  const nbv = t.costMinor - accumulatedBefore;
  const charge = divRoundHalfAwayFromZero(nbv * BigInt(t.decliningRateBp ?? 0), 120000n);
  return charge > remaining ? remaining : charge;
}
function depreciationSchedule(t, opts = {}) {
  validateAssetTerms(t);
  const from = opts.fromMonth && monthsBetween(t.startMonth, opts.fromMonth) > 0 ? opts.fromMonth : t.startMonth;
  let acc = opts.accumulatedBefore ?? 0n;
  const rows = [];
  const last = t.usefulLifeMonths - 1;
  const stop = opts.throughMonth ? Math.min(last, monthsBetween(t.startMonth, opts.throughMonth)) : last;
  for (let i = monthsBetween(t.startMonth, from); i <= stop; i++) {
    const charge = monthlyCharge(t, i, acc);
    acc += charge;
    rows.push({
      month: addMonths(t.startMonth, i),
      index: i,
      chargeMinor: charge,
      accumulatedMinor: acc,
      netBookValueMinor: t.costMinor - acc
    });
  }
  return rows;
}
function buildDepreciationPosting(input) {
  const money = (minor) => Money.of(minor, input.currency);
  const lines = [];
  for (const l of input.lines) {
    if (l.amountMinor < 0n)
      throw new LedgerError("ASSET_TERMS", "a depreciation charge cannot be negative");
    if (l.amountMinor === 0n) continue;
    const description = `Depreciation ${l.label}`;
    lines.push({ accountId: l.expenseAccountId, debit: money(l.amountMinor), description });
    lines.push({ accountId: l.accumulatedAccountId, credit: money(l.amountMinor), description });
  }
  if (lines.length === 0)
    throw new LedgerError("ASSET_TERMS", "there is no depreciation to post for this month");
  const [y, m] = input.month.split("-");
  return {
    orgId: input.orgId,
    date: monthEnd(input.month),
    sourceType: "depreciation",
    ...input.runId ? { sourceId: input.runId } : {},
    memo: `Depreciation ${m}/${y}`,
    lines
  };
}
var disposalGainLoss = (cost, accumulated, proceeds) => proceeds - (cost - accumulated);
function buildDisposalPosting(d) {
  if (d.proceedsMinor < 0n)
    throw new LedgerError("ASSET_TERMS", "disposal proceeds cannot be negative");
  if (d.accumulatedMinor < 0n || d.accumulatedMinor > d.costMinor)
    throw new LedgerError("ASSET_TERMS", "accumulated depreciation must be between 0 and cost");
  if (d.proceedsMinor > 0n && !d.proceedsAccountId)
    throw new LedgerError("ASSET_TERMS", "choose the account the proceeds went to");
  const money = (minor) => Money.of(minor, d.currency);
  const description = `Disposal ${d.label}`;
  const lines = [];
  if (d.accumulatedMinor > 0n)
    lines.push({
      accountId: d.accumulatedAccountId,
      debit: money(d.accumulatedMinor),
      description
    });
  if (d.proceedsMinor > 0n)
    lines.push({ accountId: d.proceedsAccountId, debit: money(d.proceedsMinor), description });
  lines.push({ accountId: d.assetAccountId, credit: money(d.costMinor), description });
  const gain = disposalGainLoss(d.costMinor, d.accumulatedMinor, d.proceedsMinor);
  if (gain > 0n)
    lines.push({
      accountId: d.gainLossAccountId,
      credit: money(gain),
      description: `Gain on ${description.toLowerCase()}`
    });
  if (gain < 0n)
    lines.push({
      accountId: d.gainLossAccountId,
      debit: money(-gain),
      description: `Loss on ${description.toLowerCase()}`
    });
  return {
    orgId: d.orgId,
    date: d.date,
    sourceType: "asset_disposal",
    sourceId: d.assetId,
    memo: description,
    lines
  };
}
function vatShareOf(openMinor, totalMinor, taxMinor) {
  if (openMinor < 0n || totalMinor <= 0n || taxMinor < 0n || openMinor > totalMinor)
    throw new LedgerError("WRITE_OFF", "open balance must be between zero and the invoice total");
  return divRoundHalfAwayFromZero(openMinor * taxMinor, totalMinor);
}
function buildWriteOffPosting(w) {
  if (w.amountMinor <= 0n)
    throw new LedgerError("WRITE_OFF", "there is nothing left to write off on this invoice");
  if (w.reliefVatMinor < 0n || w.reliefVatMinor >= w.amountMinor)
    throw new LedgerError("WRITE_OFF", "VAT relief must be less than the amount written off");
  if (w.reliefVatMinor > 0n && !w.vatControlAccountId)
    throw new LedgerError("WRITE_OFF", "VAT relief needs a VAT control account");
  const money = (minor) => Money.of(minor, w.currency);
  const lines = [
    {
      accountId: w.badDebtAccountId,
      debit: money(w.amountMinor - w.reliefVatMinor),
      contactId: w.contactId,
      description: w.memo
    }
  ];
  if (w.reliefVatMinor > 0n)
    lines.push({
      accountId: w.vatControlAccountId,
      debit: money(w.reliefVatMinor),
      contactId: w.contactId,
      description: "VAT bad-debt relief"
    });
  lines.push({
    accountId: w.receivableAccountId,
    credit: money(w.amountMinor),
    contactId: w.contactId,
    description: w.memo
  });
  return {
    orgId: w.orgId,
    date: w.date,
    sourceType: "write_off",
    sourceId: w.writeOffId,
    memo: w.memo,
    lines
  };
}

// src/payment-accounts.ts
var PAYMENT_ACCOUNT_DETAIL_TYPES = [
  // Bank
  "bank",
  "savings",
  "cash_on_hand",
  // Credit card
  "credit_card",
  // Current assets
  "undeposited_funds",
  "inventory",
  "prepaid",
  "employee_advances",
  "allowance_for_bad_debts",
  "loans_to_others",
  "other_current_asset",
  // Current liabilities
  "accrued_liabilities",
  "payroll_liabilities",
  "customer_deposits",
  "short_term_loans",
  "other_current_liability"
];
var SET = new Set(PAYMENT_ACCOUNT_DETAIL_TYPES);
var isPaymentAccount = (detailType) => SET.has(detailType);
var PAYMENT_ACCOUNT_MESSAGE = "choose a payment account";

// src/inventory.ts
var QTY_SCALE = 6;
var ONE = 10n ** BigInt(QTY_SCALE);
function parseQuantity(text) {
  const m = /^(\d+)(?:\.(\d{1,6}))?$/.exec(text.trim());
  if (!m)
    throw new LedgerError(
      "INVENTORY_QUANTITY",
      `quantity must be a number with at most 6 decimals, got "${text}"`
    );
  return BigInt(m[1]) * ONE + BigInt((m[2] ?? "").padEnd(QTY_SCALE, "0") || "0");
}
function formatQuantity(micro) {
  const neg = micro < 0n;
  const abs = neg ? -micro : micro;
  const int = abs / ONE;
  const frac = (abs % ONE).toString().padStart(QTY_SCALE, "0").replace(/0+$/, "");
  return `${neg ? "-" : ""}${int}${frac ? `.${frac}` : ""}`;
}
function issueFifo(layers, quantity) {
  if (quantity <= 0n) throw new LedgerError("INVENTORY_QUANTITY", "issue a quantity above zero");
  let left = quantity;
  const consumptions = [];
  for (const l of layers) {
    if (left === 0n) break;
    if (l.remainingQuantity <= 0n) continue;
    const take = left < l.remainingQuantity ? left : l.remainingQuantity;
    const costMinor = take === l.remainingQuantity ? l.remainingCostMinor : divRoundHalfAwayFromZero(l.remainingCostMinor * take, l.remainingQuantity);
    consumptions.push({ layerId: l.id, quantity: take, costMinor });
    left -= take;
  }
  if (left > 0n) {
    const onHand = layers.reduce(
      (s, l) => s + (l.remainingQuantity > 0n ? l.remainingQuantity : 0n),
      0n
    );
    throw new LedgerError(
      "INVENTORY_SHORT",
      `only ${formatQuantity(onHand)} on hand; ${formatQuantity(quantity)} needed`,
      { onHand: formatQuantity(onHand), needed: formatQuantity(quantity) }
    );
  }
  return { consumptions, costMinor: consumptions.reduce((s, c) => s + c.costMinor, 0n) };
}
function costAt(unitCostMinor, quantity) {
  return divRoundHalfAwayFromZero(unitCostMinor * quantity, ONE);
}
function unitCost(costMinor, quantity) {
  return quantity > 0n ? divRoundHalfAwayFromZero(costMinor * ONE, quantity) : 0n;
}

// src/features.ts
var FEATURES = [
  "invoices",
  "sales",
  "purchases",
  "expenses",
  "inventory",
  "banking",
  "reports",
  "taxes"
];
var POSTING_MODES = ["staff", "approval", "direct"];
var POSTABLE_FEATURES = [
  "invoices",
  "sales",
  "purchases",
  "expenses",
  "inventory"
];
var FEATURE_INFO = {
  invoices: {
    label: "Invoices",
    covers: "Invoices, receive payment, credit notes, statements, customers",
    requires: []
  },
  sales: {
    label: "Sales",
    covers: "Quotes, sales orders, sales receipts, delivery challans (on top of invoices)",
    requires: [["invoices"]]
  },
  purchases: {
    label: "Purchases",
    covers: "Bills, purchase orders, pay bills, debit notes, vendors",
    requires: []
  },
  expenses: { label: "Expenses", covers: "Expenses paid at once, vendors", requires: [] },
  inventory: {
    label: "Inventory",
    covers: "Inventory items, quantity adjustments, stock reports, barcode scanning",
    requires: [["invoices", "sales"], ["purchases"]]
  },
  banking: {
    label: "Banking",
    covers: "Bank transactions, statement import, rules, reconcile, transfers",
    requires: []
  },
  reports: { label: "Reports", covers: "The reports hub and every report", requires: [] },
  taxes: {
    label: "Taxes",
    covers: "GST or VAT returns, tax rates, e-invoicing, corporate tax",
    requires: []
  }
};
var DEFAULT_FEATURES = Object.freeze(
  Object.fromEntries(FEATURES.map((f) => [f, "staff"]))
);
var isFeature = (v) => typeof v === "string" && FEATURES.includes(v);
var isPostingMode = (v) => typeof v === "string" && POSTING_MODES.includes(v);
var KIND_FEATURE = {
  invoice: "invoices",
  credit_note: "invoices",
  receive_payment: "invoices",
  statement: "invoices",
  quote: "sales",
  sales_order: "sales",
  sales_receipt: "sales",
  delivery_challan: "sales",
  bill: "purchases",
  debit_note: "purchases",
  purchase_order: "purchases",
  pay_bills: "purchases",
  expense: "expenses",
  inventory_adjustment: "inventory"
};
function featureOfKind(kind) {
  const f = KIND_FEATURE[kind];
  if (!f) throw new LedgerError("FEATURE_UNKNOWN", `no feature covers "${kind}"`, { kind });
  return f;
}
function featureViolations(set) {
  const out = [];
  for (const [feature, posting] of Object.entries(set)) {
    if (!isFeature(feature)) {
      out.push(`"${feature}" is not a feature`);
      continue;
    }
    if (!isPostingMode(posting)) {
      out.push(`"${String(posting)}" is not a posting mode`);
      continue;
    }
    if (posting !== "staff" && !POSTABLE_FEATURES.includes(feature))
      out.push(`${FEATURE_INFO[feature].label}: only accounting staff post here`);
    for (const group of FEATURE_INFO[feature].requires) {
      if (!group.some((g) => g in set))
        out.push(
          `${FEATURE_INFO[feature].label} needs ${group.map((g) => FEATURE_INFO[g].label).join(" or ")}`
        );
    }
  }
  return out;
}
function assertFeatureSet(set) {
  const problems = featureViolations(set);
  if (problems.length) throw new LedgerError("FEATURE_RULES", problems.join("; "), { problems });
}
export {
  AGEING_BUCKET_KEYS,
  AGEING_BUCKET_LABELS,
  CURRENCIES,
  DEFAULT_FEATURES,
  Decimal,
  FEATURES,
  FEATURE_INFO,
  LedgerError,
  Money,
  PAYMENT_ACCOUNT_DETAIL_TYPES,
  PAYMENT_ACCOUNT_MESSAGE,
  POSTABLE_FEATURES,
  POSTING_MODES,
  QTY_SCALE,
  RUN_REPORTS,
  SOURCE_TYPES,
  VAT201_BOXES,
  VAT_ONLY_BOXES,
  addDays,
  addMonths,
  assertFeatureSet,
  buildDepreciationPosting,
  buildDisposalPosting,
  buildOpeningBalancePosting,
  buildOpeningItemPosting,
  buildPurchasePosting,
  buildRealisedFxPosting,
  buildReceiptPosting,
  buildRevaluationPosting,
  buildSalesPosting,
  buildVendorPaymentPosting,
  buildWriteOffPosting,
  compareFigures,
  computePurchaseLine,
  computePurchaseTotals,
  computeRevaluation,
  computeSalesLine,
  computeSalesTotals,
  convertMinorUnits,
  costAt,
  counterMoney,
  defaultDecliningRateBp,
  depreciationSchedule,
  describeSchedule,
  disposalGainLoss,
  divRoundHalfAwayFromZero,
  exponentOf,
  featureOfKind,
  featureViolations,
  firstOccurrenceOnOrAfter,
  formatMinor,
  formatQuantity,
  fxFields,
  isCurrencyCode,
  isFeature,
  isPaymentAccount,
  isPostingMode,
  isValidIsoDate,
  issueFifo,
  lineTracking,
  matchAccount,
  mirrorLines,
  monthEnd,
  monthOf2 as monthOf,
  monthlyCharge,
  monthsBetween,
  mulRound,
  nextOccurrenceAfter,
  normLabel,
  occurrenceAt,
  parseCsvText,
  parseFxRate,
  parseQboAgeingDetail,
  parseQboAgeingSummary,
  parseQboFigures,
  parseQboSummary,
  parseQboTrialBalance,
  parseQuantity,
  parseReportAmount,
  parseReportDate,
  parseVat201,
  preparePosting,
  realisedFx,
  reportPeriodFrom,
  settlementBase,
  splitAccountLabel,
  splitRecoverable,
  toBaseMinor,
  trackingOf,
  trimRate,
  tryReportAmount,
  unitCost,
  validateAssetTerms,
  validateSchedule,
  vatFigureKey,
  vatSettlementLines,
  vatShareOf,
  weekdayOf
};
//# sourceMappingURL=index.js.map